import { useEffect, useRef } from 'react';
import estilos from './RedDeNodos.module.css';

/** Puntos que caben según el tamaño de la ventana; nunca más de estos. */
const MAXIMO = 120;

/** Un punto por cada tantos píxeles de pantalla. Más área, más puntos. */
const DENSIDAD_MOVIL = 13_000;
const DENSIDAD_ESCRITORIO = 18_000;

/** Hasta qué distancia dos puntos se unen con una línea. */
const ENLACE_MOVIL = 110;
const ENLACE_ESCRITORIO = 150;

/** Radio dentro del cual los puntos se apartan del ratón. */
const REPULSION = 130;

interface Punto {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

/**
 * Red de nodos animada: el fondo de la web de VenIoT.
 *
 * Puntos que flotan y se unen con una línea cuando se acercan, apartándose del
 * ratón. Es la misma idea que la portada del sitio —portada y acceso son la
 * misma puerta y deben parecerlo— reescrita como componente para que se limpie
 * solo al desmontarse.
 *
 * Es decoración pura: va detrás de todo, no recibe pulsaciones y está oculto a
 * los lectores de pantalla.
 */
export function RedDeNodos() {
  const lienzo = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = lienzo.current;
    if (!cv) return;

    /*
     * Quien pide reducir el movimiento se queda sin animación. Un fondo de
     * puntos moviéndose sin parar es justo lo que provoca malestar a quien
     * activa esa preferencia, y aquí no aporta nada más que adorno.
     */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const esMovil = window.innerWidth < 768;
    const densidad = esMovil ? DENSIDAD_MOVIL : DENSIDAD_ESCRITORIO;
    const enlace = esMovil ? ENLACE_MOVIL : ENLACE_ESCRITORIO;

    let ancho = 0;
    let alto = 0;

    const redimensionar = () => {
      const escala = window.devicePixelRatio || 1;
      ancho = window.innerWidth;
      alto = window.innerHeight;
      cv.width = ancho * escala;
      cv.height = alto * escala;
      cv.style.width = `${ancho}px`;
      cv.style.height = `${alto}px`;
    };

    redimensionar();

    const cuantos = Math.min(MAXIMO, Math.round((ancho * alto) / densidad));
    const puntos: Punto[] = Array.from({ length: cuantos }, () => ({
      x: Math.random() * ancho,
      y: Math.random() * alto,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.6 + 0.7,
    }));

    // Fuera de la pantalla mientras no haya ratón: así nada se aparta al entrar.
    const raton = { x: -9999, y: -9999 };

    const alMover = (evento: MouseEvent) => {
      raton.x = evento.clientX;
      raton.y = evento.clientY;
    };

    const alSalir = () => {
      raton.x = -9999;
      raton.y = -9999;
    };

    window.addEventListener('resize', redimensionar, { passive: true });
    window.addEventListener('mousemove', alMover, { passive: true });
    window.addEventListener('mouseout', alSalir, { passive: true });

    let animacion = 0;

    const dibujar = () => {
      animacion = requestAnimationFrame(dibujar);

      // En segundo plano no se dibuja: gastaría batería sin que nadie mire.
      if (document.hidden) return;

      const escala = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.save();
      ctx.scale(escala, escala);

      for (const p of puntos) {
        const dx = p.x - raton.x;
        const dy = p.y - raton.y;
        const distancia = Math.hypot(dx, dy);

        if (distancia < REPULSION && distancia > 0) {
          p.x += (dx / distancia) * 0.7;
          p.y += (dy / distancia) * 0.7;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Rebotan en los bordes en vez de perderse fuera de la pantalla.
        if (p.x < 0 || p.x > ancho) p.vx *= -1;
        if (p.y < 0 || p.y > alto) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 160, 255, 0.55)';
        ctx.fill();
      }

      /*
       * Las líneas se dibujan comparando cada par de puntos. Con el tope de 120
       * son unas siete mil comprobaciones por fotograma, que es asumible; sin
       * tope, en una pantalla grande se dispararía y el fondo se comería la
       * fluidez de la propia pantalla de acceso.
       */
      for (let i = 0; i < puntos.length; i += 1) {
        for (let j = i + 1; j < puntos.length; j += 1) {
          const a = puntos[i]!;
          const b = puntos[j]!;
          const distancia = Math.hypot(a.x - b.x, a.y - b.y);

          if (distancia >= enlace) continue;

          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          // Cuanto más cerca, más marcada: da sensación de red viva.
          ctx.strokeStyle = `rgba(0, 120, 255, ${(1 - distancia / enlace) * 0.16})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      ctx.restore();
    };

    dibujar();

    return () => {
      cancelAnimationFrame(animacion);
      window.removeEventListener('resize', redimensionar);
      window.removeEventListener('mousemove', alMover);
      window.removeEventListener('mouseout', alSalir);
    };
  }, []);

  return <canvas ref={lienzo} className={estilos.lienzo} aria-hidden />;
}
